import React from 'react';
import { Grid, TextField, FormControl, InputLabel, MenuItem, Select } from '@mui/material';

interface AddressFieldsProps {
  values: any;
  touched: any;
  errors: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  setFieldValue?: (field: string, value: any) => void;
}

const AddressFields: React.FC<AddressFieldsProps> = ({ values, touched, errors, handleChange, handleBlur, setFieldValue }) => {
  // Fix for MUI Select typing
  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    if (setFieldValue) {
      setFieldValue(name, value);
    } else {
      handleChange(event);
    }
  };

  const fetchAddressFromViaCep = async (cep: string) => {
    try {
      // Remove non-numeric characters
      const cleanCep = cep.replace(/\D/g, '');

      // Check if CEP has 8 digits
      if (cleanCep.length !== 8) {
        return;
      }

      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      // Check if ViaCEP returned an error
      if (data.erro) {
        return;
      }

      // Auto-fill the address fields using setFieldValue if available
      if (setFieldValue) {
        if (data.logradouro) setFieldValue('street', data.logradouro);
        if (data.bairro) setFieldValue('neighborhood', data.bairro);
        if (data.localidade) setFieldValue('city', data.localidade);
        if (data.uf) setFieldValue('stateUf', data.uf);
      }
    } catch (error) {
      // Silently fail - if ViaCEP fails, simply do not fill
    }
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleChange(e);

    // Trigger ViaCEP fetch when CEP has 8 digits
    const cleanCep = value.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      fetchAddressFromViaCep(value);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <TextField
          label="CEP"
          placeholder="00000-000"
          fullWidth
          required
          type="text"
          variant="standard"
          name="postalCode"
          value={values.postalCode}
          onChange={handlePostalCodeChange}
          onBlur={handleBlur}
          error={Boolean(touched.postalCode && errors.postalCode)}
          helperText={touched.postalCode && errors.postalCode}
          InputLabelProps={{
            shrink: true,
            sx: { fontSize: '16px', fontWeight: 500 },
          }}
          InputProps={{ sx: { fontSize: '14px' } }}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <TextField
          label="Logradouro"
          placeholder="Nome da rua ou avenida"
          fullWidth
          required
          variant="standard"
          name="street"
          value={values.street}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.street && errors.street)}
          helperText={touched.street && errors.street}
          InputLabelProps={{
            shrink: true,
            sx: { fontSize: '16px', fontWeight: 500 },
          }}
          InputProps={{ sx: { fontSize: '14px' } }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Número"
          placeholder="Número do endereço"
          fullWidth
          required
          variant="standard"
          name="streetNumber"
          value={values.streetNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.streetNumber && errors.streetNumber)}
          helperText={touched.streetNumber && errors.streetNumber}
          InputLabelProps={{
            shrink: true,
            sx: { fontSize: '16px', fontWeight: 500 },
          }}
          InputProps={{ sx: { fontSize: '14px' } }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Complemento (opcional)"
          placeholder="Apto, sala, bloco..."
          fullWidth
          variant="standard"
          name="addressComplement"
          value={values.addressComplement}
          onChange={handleChange}
          onBlur={handleBlur}
          InputLabelProps={{
            shrink: true,
            sx: { fontSize: '16px', fontWeight: 500 },
          }}
          InputProps={{ sx: { fontSize: '14px' } }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Bairro"
          placeholder="Nome do bairro"
          fullWidth
          required
          variant="standard"
          name="neighborhood"
          value={values.neighborhood}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.neighborhood && errors.neighborhood)}
          helperText={touched.neighborhood && errors.neighborhood}
          InputLabelProps={{
            shrink: true,
            sx: { fontSize: '16px', fontWeight: 500 },
          }}
          InputProps={{ sx: { fontSize: '14px' } }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Cidade"
          placeholder="Nome da cidade"
          fullWidth
          required
          variant="standard"
          name="city"
          value={values.city}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.city && errors.city)}
          helperText={touched.city && errors.city}
          InputLabelProps={{
            shrink: true,
            sx: { fontSize: '16px', fontWeight: 500 },
          }}
          InputProps={{ sx: { fontSize: '14px' } }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required variant="standard">
          <InputLabel shrink sx={{ fontSize: '16px', fontWeight: 500 }}>
            Estado
          </InputLabel>
          <Select
            label="Estado"
            name="stateUf"
            value={values.stateUf}
            onChange={handleSelectChange}
            onBlur={handleBlur}
            sx={{ fontSize: '14px' }}
            inputProps={{ sx: { fontSize: '14px' } }}
          >
            <MenuItem value="AC">AC</MenuItem>
            <MenuItem value="AL">AL</MenuItem>
            <MenuItem value="AP">AP</MenuItem>
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="BA">BA</MenuItem>
            <MenuItem value="CE">CE</MenuItem>
            <MenuItem value="DF">DF</MenuItem>
            <MenuItem value="ES">ES</MenuItem>
            <MenuItem value="GO">GO</MenuItem>
            <MenuItem value="MA">MA</MenuItem>
            <MenuItem value="MT">MT</MenuItem>
            <MenuItem value="MS">MS</MenuItem>
            <MenuItem value="MG">MG</MenuItem>
            <MenuItem value="PA">PA</MenuItem>
            <MenuItem value="PB">PB</MenuItem>
            <MenuItem value="PR">PR</MenuItem>
            <MenuItem value="PE">PE</MenuItem>
            <MenuItem value="PI">PI</MenuItem>
            <MenuItem value="RJ">RJ</MenuItem>
            <MenuItem value="RN">RN</MenuItem>
            <MenuItem value="RS">RS</MenuItem>
            <MenuItem value="RO">RO</MenuItem>
            <MenuItem value="RR">RR</MenuItem>
            <MenuItem value="SC">SC</MenuItem>
            <MenuItem value="SP">SP</MenuItem>
            <MenuItem value="SE">SE</MenuItem>
            <MenuItem value="TO">TO</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default AddressFields;
